import React, { useCallback } from 'react';
import InlineEdit from '@atlaskit/inline-edit';
import { Inline, xcss, Box, Text } from '@atlaskit/primitives';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useStandupTimerStore } from '../../../controllers/timer-store';
import messages from './messages';
import {
	convertSecondsToMinutesAndSeconds,
	formatTime,
	convertMinutesAndSecondsToSeconds,
} from './utils';

const TimerInput = () => {
	const [
		{ totalSeconds: timerTotalSeconds, elapsedSeconds },
		{ setTotalSeconds, resetElapsedSeconds },
	] = useStandupTimerStore();
	const totalSecondsRemaining = timerTotalSeconds - elapsedSeconds;
	const { minutes, seconds } = convertSecondsToMinutesAndSeconds(totalSecondsRemaining);

	const { formatMessage } = useIntl();

	const handleInputChange = useCallback(
		(newTotalSeconds: number) => {
			setTotalSeconds(newTotalSeconds);
			resetElapsedSeconds();
		},
		[setTotalSeconds, resetElapsedSeconds],
	);

	return (
		<Inline alignInline="center" alignBlock="center" xcss={inlineStyles}>
			<Box xcss={wrapperStyles} testId="standups.ui.timer.timer-input.minutes">
				<InlineEdit
					hideActionButtons
					defaultValue={minutes}
					editView={({ errorMessage, ...fieldProps }) => (
						<Textfield
							{...fieldProps}
							isCompact
							maxLength={2}
							type="text"
							placeholder="00"
							appearance="subtle"
							width={32}
						/>
					)}
					readView={() => (
						<Box
							aria-label={formatMessage(messages.minutesNonfinal)}
							role="textbox"
							xcss={readViewContainerStyles}
						>
							{formatTime(minutes)}
						</Box>
					)}
					onConfirm={(value) => {
						const totalSeconds = convertMinutesAndSecondsToSeconds(Number(value), seconds);
						handleInputChange(totalSeconds);
					}}
				/>
			</Box>
			<Text weight="semibold" size="large">
				:
			</Text>
			<Box xcss={wrapperStyles}>
				<InlineEdit
					hideActionButtons
					defaultValue={seconds}
					editView={({ errorMessage, ...fieldProps }) => (
						<Textfield
							{...fieldProps}
							isCompact
							maxLength={2}
							type="text"
							placeholder="00"
							appearance="subtle"
							width={32}
						/>
					)}
					readView={() => (
						<Box
							aria-label={formatMessage(messages.secondsNonfinal)}
							role="textbox"
							testId="standups.ui.timer.timer-input.seconds"
							xcss={readViewContainerStyles}
						>
							{formatTime(seconds)}
						</Box>
					)}
					onConfirm={(value) => {
						const totalSeconds = convertMinutesAndSecondsToSeconds(minutes, Number(value));
						handleInputChange(totalSeconds);
					}}
				/>
			</Box>
		</Inline>
	);
};

export default TimerInput;

const wrapperStyles = xcss({
	font: token('font.heading.large'),
	alignItems: 'center',
	position: 'relative',
	top: 'space.negative.050',
});

const inlineStyles = xcss({
	paddingRight: 'space.050',
});

const readViewContainerStyles = xcss({
	paddingBlock: 'space.050',
	paddingInline: 'space.025',
	wordBreak: 'break-word',
});
